import { alpha, Card, Center, Stack, Text, Title, useComputedColorScheme } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@/api';
import { LoginCallback } from '../components/Login/LoginCallback';
import { useLanguageAwareRouting } from '../hooks/useLanguageAwareRouting';

export interface LoginCallbackPageProps {
  setSession: (session: Session) => void;
  apiBaseUrl: string;
  onLoginSuccess?: () => void; // If not provided, will navigate to '/account'
  backgroundImage?: string; // Default: '/and-machines-vqTWfa4DjEk.jpg'
  titleGradient?: { from: string; to: string }; // Default: { from: 'pink', to: 'yellow' }
  titleClassName?: string; // CSS class for the title styling
}

export function LoginCallbackPage(props: LoginCallbackPageProps) {
  const { t } = useTranslation();
  const isDark = useComputedColorScheme('light') === 'dark';
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  // Login failed — the big "Logging in…" banner is misleading once we know that.
  const [failed, setFailed] = useState(false);

  const {
    setSession,
    onLoginSuccess,
    titleGradient = { from: 'pink', to: 'yellow' },
    titleClassName,
  } = props;

  const loginDoneCb = () => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      nav(createLanguageURL('/account'));
    }
  };

  return (
    <Center h="100vh" px="md">
      <Stack w="100%" maw={400} align="center">
        {!failed && (
          <Title order={1} className={titleClassName} ta="center">
            <Text inherit variant="gradient" component="span" gradient={titleGradient}>
              {t('login.loggingIn')}
            </Text>
          </Title>
        )}
        <Card
          maw={400}
          w="100%"
          p="xl"
          radius="lg"
          withBorder
          shadow="sm"
          bg={isDark ? alpha('var(--mantine-color-gray-8)', 0.6) : 'var(--mantine-color-default)'}
        >
          <LoginCallback
            setSession={setSession}
            loginDoneCb={loginDoneCb}
            apiBaseUrl={props.apiBaseUrl}
            loginHref={`#${createLanguageURL('/login')}`}
            onError={() => setFailed(true)}
          />
        </Card>
      </Stack>
    </Center>
  );
}
